import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { primaryButtonClass } from '@/components/common/formStyles'
import { ForkStrategyModal } from '@/components/strategy/ForkStrategyModal'
import { RuleSection } from '@/components/strategy/RuleSection'
import { useConfirmStrategy, useCurrentStrategy, useSeedStrategy } from '@/lib/useStrategy'
import type { Rule } from '@/types/strategy'

function groupRules(rules: Rule[]): {
  positions: Rule[]
  clusters: Rule[]
  stopLoss: Rule[]
  forbidden: Rule[]
  rebalance: Rule[]
  tax: Rule[]
} {
  const positions: Rule[] = []
  const clusters: Rule[] = []
  for (const rule of rules) {
    if (rule.category !== 'allocation') continue
    if (rule.name.startsWith('cluster_')) clusters.push(rule)
    else positions.push(rule)
  }
  return {
    positions,
    clusters,
    stopLoss: rules.filter((r) => r.category === 'stop_loss'),
    forbidden: rules.filter((r) => r.category === 'forbidden'),
    rebalance: rules.filter((r) => r.category === 'rebalance'),
    tax: rules.filter((r) => r.category === 'tax'),
  }
}

export function StrategyPage() {
  const current = useCurrentStrategy()
  const seed = useSeedStrategy()
  const confirm = useConfirmStrategy()
  const [forkOpen, setForkOpen] = useState(false)

  const isNoActive =
    current.isError &&
    (current.error as { response?: { status?: number } })?.response?.status === 404

  const handleSeed = async () => {
    await seed.mutateAsync()
  }

  const handleConfirm = async (id: number) => {
    await confirm.mutateAsync(id)
  }

  if (current.isLoading) {
    return (
      <AppShell>
        <p className="font-mono text-sm text-muted">Lade Strategy …</p>
      </AppShell>
    )
  }

  if (isNoActive) {
    return (
      <AppShell>
        <article className="fade-in max-w-2xl">
          <header>
            <p className="tracked-caps text-[10px] text-faint">Strategy</p>
            <h2 className="mt-2 text-2xl text-ink">Keine aktive Strategy</h2>
            <p className="mt-3 font-mono text-xs text-muted">
              Lege die initiale Version v1.0 aus SEXTANT.md §3 an und bestätige sie. Danach laufen
              Stop-Loss, Profit-Taking, Rebalancing-Checks gegen dieses Regelwerk.
            </p>
          </header>
          <div className="mt-8 flex flex-col gap-4">
            <button
              type="button"
              onClick={handleSeed}
              disabled={seed.isPending}
              className={primaryButtonClass}
            >
              {seed.isPending ? 'Erzeuge …' : 'v1.0 anlegen'}
            </button>
            {seed.isError && (
              <p role="alert" className="text-sm text-loss">
                {(seed.error as Error).message}
              </p>
            )}
            {seed.data && seed.data.status === 'draft' && (
              <div className="border border-hairline bg-void px-4 py-3">
                <p className="font-mono text-xs text-muted">
                  v{seed.data.version} als Draft erzeugt mit {seed.data.rules.length} Regeln.
                  Bestätigen?
                </p>
                <button
                  type="button"
                  onClick={() => handleConfirm(seed.data!.id)}
                  disabled={confirm.isPending}
                  className={`${primaryButtonClass} mt-3`}
                >
                  {confirm.isPending ? 'Bestätige …' : 'v' + seed.data.version + ' aktivieren'}
                </button>
              </div>
            )}
            <p className="font-mono text-[11px] text-faint">
              Detaillierte Beschreibung:{' '}
              <Link to="/strategy/history" className="text-bernstein hover:underline">
                Versions-Historie
              </Link>
            </p>
          </div>
        </article>
      </AppShell>
    )
  }

  if (current.isError) {
    return (
      <AppShell>
        <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
          <p className="tracked-caps text-[10px] text-loss">Fehler</p>
          <p className="mt-2 font-mono text-sm text-ink">Strategy konnte nicht geladen werden.</p>
          <p className="mt-1 font-mono text-xs text-muted">{(current.error as Error).message}</p>
        </div>
      </AppShell>
    )
  }

  const strategy = current.data!
  const groups = groupRules(strategy.rules)

  return (
    <AppShell>
      <article className="fade-in max-w-4xl">
        <header className="flex flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div>
            <p className="tracked-caps text-[10px] text-faint">Strategy</p>
            <h2 className="mt-2 text-2xl text-ink">v{strategy.version}</h2>
            <p className="mt-1 font-mono text-xs text-muted">
              {strategy.status} · {strategy.rules.length} Regeln · seit{' '}
              {strategy.activated_at
                ? new Date(strategy.activated_at).toLocaleDateString('de-DE')
                : '—'}
            </p>
            {strategy.reason && (
              <p className="mt-2 max-w-prose font-mono text-[11px] text-muted">{strategy.reason}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
            {strategy.status === 'draft' && (
              <Link to={`/strategy/${strategy.id}/edit`} className={primaryButtonClass}>
                Bearbeiten
              </Link>
            )}
            <button
              type="button"
              onClick={() => setForkOpen(true)}
              className="border border-hairline px-3 py-2 font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
            >
              Fork →
            </button>
            <Link
              to="/strategy/history"
              className="font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
            >
              Historie →
            </Link>
          </div>
        </header>

        <div className="mt-10 flex flex-col gap-12">
          <RuleSection
            title="Allokation (Ziel)"
            rules={groups.positions}
            fieldLabels={{
              ticker: 'Ticker',
              isin: 'ISIN',
              label: 'Name',
              target_pct: 'Ziel %',
              role: 'Rolle',
              asset_kind: 'Typ',
            }}
          />
          <RuleSection
            title="Cluster"
            rules={groups.clusters}
            fieldLabels={{
              label: 'Cluster',
              members: 'Mitglieder',
              max_pct: 'Max %',
              min_pct: 'Min %',
              action: 'Aktion',
            }}
          />
          <RuleSection
            title="Stop-Loss"
            rules={groups.stopLoss}
            fieldLabels={{
              ticker: 'Ticker',
              trailing_pct: 'Trailing %',
              rationale: 'Begründung',
              alert_only: 'nur Alert',
              active: 'aktiv',
            }}
            hideKeys={['tiers']}
          />
          <RuleSection
            title="Verbotene Aktionen"
            rules={groups.forbidden}
            fieldLabels={{
              rule: 'Regel',
              severity: 'Schweregrad',
            }}
          />
          <RuleSection
            title="Rebalancing"
            rules={groups.rebalance}
            fieldLabels={{
              rule: 'Regel',
              frequency: 'Frequenz',
              threshold_pct: 'Schwelle %',
              severity: 'Schweregrad',
            }}
          />
          <RuleSection
            title="Steuer"
            rules={groups.tax}
            fieldLabels={{
              rule: 'Regel',
              holding_years: 'Haltefrist (Jahre)',
              kest_pct: 'KESt %',
            }}
          />
        </div>
      </article>

      <ForkStrategyModal
        open={forkOpen}
        onClose={() => setForkOpen(false)}
        parentId={strategy.id}
        parentVersion={strategy.version}
      />
    </AppShell>
  )
}
